import path from 'path'
import _ from 'lodash'
import { translateName } from '../../../../utils/i18n'

function stripHtml(html = '') {
    return (html || '')
        .replace(/<\/p>/gi, '\n')
        .replace(/<\/li>/gi, '\n')
        .replace(/<br\s*\/?>/gi, '\n')
        .replace(/<[^>]+>/g, '')
        .replace(/&nbsp;/g, ' ')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&amp;/g, '&')
        .replace(/\n{3,}/g, '\n\n')
        .trim()
}

function getScoreLevel(score) {
    if (score === null || score === undefined) return null
    if (score <= 50) return 1
    if (score <= 75) return 2
    if (score < 95) return 3
    return 4
}

const LEVEL_COLORS = {
    1: 'FFFF0000',
    2: 'FFFFC000',
    3: 'FFFFFF00',
    4: 'FF92D050'
}

function getAnomalyBgColor(conformityRateId) {
    switch (conformityRateId) {
        case '0':  return 'E69180' // 50% version of CC2200
        case '25': return 'FF8080' // 50% version of FF0000
        case '50': return 'FFC680' // 50% version of FF8C00
        case '75': return 'FFEB80' // 50% version of FFD700
        default: return null
    }
}

function applyFill(cell, argb) {
    if (!argb) return
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb } }
}

function configureAuditReportWorkbook({ auditPreparation, controls, language, context }) {
    const lang = language || 'fr'
    const tl = (key, opts) => context.tl(key, lang, opts)

    return workbook => {
        const ws = workbook.addWorksheet(auditPreparation.auditNumber || 'Rapport')

        ws.getColumn('A').width = 18.5
        ws.getColumn('B').width = 37.83
        ws.getColumn('C').width = 29.83
        ws.getColumn('D').width = 22.83
        ws.getColumn('E').width = 25.83
        ws.getColumn('F').width = 6.83
        ws.getColumn('G').width = 12
        ws.getColumn('H').width = 12
        ws.getColumn('I').width = 8.5
        ws.getColumn('J').width = 20.83

        // ─── Logo header (rows 1-9) ──────────────────────────────────────────
        merge('A1:J9')
        const imgDir = path.resolve(__dirname, '../../../../img/stroili')
        const img1Id = workbook.addImage({ filename: path.join(imgDir, 'stroili-logo-nobg.png'), extension: 'png' })
        ws.addImage(img1Id, { tl: { col: 3.5, row: 0.5 }, ext: { width: 222, height: 160 } })

        function cell(coord, value, { bold = false, size = 11, fill = null, align = null, wrapText = false, numFmt = null } = {}) {
            const c = ws.getCell(coord)
            c.value = value
            c.font = { bold, size }
            if (fill) applyFill(c, fill)
            c.alignment = { horizontal: align || 'left', vertical: 'middle', wrapText }
            if (numFmt) c.numFmt = numFmt
            return c
        }

        function merge(range) { ws.mergeCells(range) }

        // ─── Header ─────────────────────────────────────────────────────────
        const store = auditPreparation.store || {}
        const storeName = store.fullName
            || (store.code && store.name ? `${store.code} - ${store.name}` : store.code || store.name || '')
        const hcNames = (auditPreparation.hierarchicalContacts || [])
            .map(hc => hc.userName || _.get(hc, 'user.name') || '').filter(Boolean).join(', ')
        const scNames = (auditPreparation.storeContacts || [])
            .map(sc => sc.userName || _.get(sc, 'user.name') || '').filter(Boolean).join(', ')

        ws.getRow(10).height = 42
        merge('A10:J10')
        cell('A10', storeName, { bold: true, size: 20, align: 'center' })

        ws.getRow(11).height = 21
        cell('A11', tl('auditHierarchyContactLine', { name: hcNames }), { bold: true, size: 16 })
        cell('H11', tl('auditDateLine', { date: auditPreparation.controlDate || '' }), { bold: true, size: 16 })

        ws.getRow(12).height = 21
        cell('A12', tl('auditStoreContactLine', { name: scNames }), { bold: true, size: 16 })

        ws.getRow(13).height = 15
        merge('A13:J13')

        // ─── Results section title ───────────────────────────────────────────
        merge('A14:J16')
        ws.getRow(14).height = 12
        ws.getRow(15).height = 12
        ws.getRow(16).height = 12
        cell('A14', tl('auditResultsTitle'), {
            bold: true, size: 16, fill: 'FFEFDADD', align: 'center'
        })

        ws.getRow(17).height = 17

        // ─── Score section ───────────────────────────────────────────────────
        ws.getRow(18).height = 30
        merge('A18:J18')
        cell('A18', tl('auditGlobalScore'), { bold: true, size: 13, fill: 'FFEFDADD', align: 'center' })
//here
        ws.getRow(19).height = 20

        const score = auditPreparation.auditScore
        const level = getScoreLevel(score)
        const levelColor = LEVEL_COLORS[level] || 'FFEFDADD'
        const scoreDisplay = score !== null && score !== undefined
            ? `${(Math.round(score * 10) / 10).toFixed(1).replace('.', ',')}%`
            : 'N/A'

        const levelLabelKeys = {
            1: 'auditLevel1Label',
            2: 'auditLevel2Label',
            3: 'auditLevel3Label',
            4: 'auditLevel4Label'
        }

        ws.getRow(20).height = 20
        ws.getRow(21).height = 20
        merge('A20:C21')
        cell('A20', level ? tl(levelLabelKeys[level]) : '', { bold: true, size: 22, fill: levelColor, align: 'center' })

        merge('E20:F20')
        cell('E20', tl('auditRankingLegend'), { bold: true, size: 12, align: 'center' })
        merge('G20:I20')
        cell('G20', tl('auditAdequacyLevel'), { bold: true, size: 12, align: 'center' })
        cell('J20', tl('auditConformityRate'), { bold: true, size: 12, align: 'center' })

        merge('E21:F21')
        cell('E21', 1, { bold: true, size: 13, fill: 'FFFF0000', align: 'center' })
        merge('G21:I21')
        cell('G21', tl('auditLevel1Label'), { size: 10 })
        cell('J21', tl('auditLevelRange1'), { size: 10 })

        ws.getRow(22).height = 20
        merge('E22:F22')
        cell('E22', 2, { bold: true, size: 13, fill: 'FFFFC000', align: 'center' })
        merge('G22:I22')
        cell('G22', tl('auditLevel2Label'), { size: 10 })
        cell('J22', tl('auditLevelRange2'), { size: 10 })

        ws.getRow(23).height = 20
        ws.getRow(24).height = 20
        merge('A23:C24')
        cell('A23', tl('auditConformityRateValue', { value: scoreDisplay }), { bold: true, size: 22, fill: levelColor, align: 'center' })

        merge('E23:F23')
        cell('E23', 3, { bold: true, size: 13, fill: 'FFFFFF00', align: 'center' })
        merge('G23:I23')
        cell('G23', tl('auditLevel3Label'), { size: 10 })
        cell('J23', tl('auditLevelRange3'), { size: 10 })

        merge('E24:F24')
        cell('E24', 4, { bold: true, size: 13, fill: 'FF92D050', align: 'center' })
        merge('G24:I24')
        cell('G24', tl('auditLevel4Label'), { size: 10 })
        cell('J24', tl('auditLevelRange4'), { size: 10 })

        ws.getRow(25).height = 17

        // ─── Themes section ──────────────────────────────────────────────────
        ws.getRow(26).height = 30
        merge('A26:J26')
        cell('A26', tl('auditCheckedThemes'), { bold: true, size: 13, fill: 'FFEFDADD', align: 'center' })

        ws.getRow(27).height = 17

        // Per-theme conformity
        const themeMap = {}
        controls.forEach(ctrl => {
            const theme = _.get(ctrl, 'auditControl.auditTheme')
            if (!theme) return
            const themeId = theme.id || (theme._id && theme._id.toString())
            if (!themeId) return
            if (!themeMap[themeId]) {
                themeMap[themeId] = {
                    code: theme.code || '',
                    name: translateName(theme.name || '', lang),
                    totalWeight: 0,
                    scoredWeight: 0
                }
            }
            const statusId = _.get(ctrl, 'status.id')
            if (['notApplicable', 'notControlled'].includes(statusId)) return
            const weight = _.get(ctrl, 'auditControl.weight.value') || 0
            const rate = parseInt(_.get(ctrl, 'conformityRate.id') || '0', 10)
            themeMap[themeId].totalWeight += weight
            themeMap[themeId].scoredWeight += (weight * rate) / 100
        })

        const themes = _.sortBy(Object.values(themeMap), 'code')

        ws.getRow(28).height = 35
        merge('A28:D28')
        cell('A28', tl('auditTheme'), { bold: true, size: 13, fill: 'FFEFDADD', align: 'center' })
        merge('E28:J28')
        cell('E28', tl('auditConformityRate'), { bold: true, size: 13, fill: 'FFEFDADD', align: 'center' })

        ws.getRow(29).height = 17

        let row = 30
        const firstThemeRow = row
        themes.forEach(theme => {
            ws.getRow(row).height = 16
            merge(`A${row}:D${row}`)
            cell(`A${row}`, `${theme.code} - ${theme.name}`, { bold: true, size: 13 })
            const fraction = theme.totalWeight > 0 ? theme.scoredWeight / theme.totalWeight : null
            merge(`E${row}:J${row}`)
            cell(`E${row}`, fraction, { size: 12, align: 'center', numFmt: '0%' })
            row++
        })

        if (themes.length > 0) {
            ws.addConditionalFormatting({
                ref: `E${firstThemeRow}:E${row - 1}`,
                rules: [{
                    priority: 1,
                    type: 'dataBar',
                    cfvo: [{ type: 'num', value: 0 }, { type: 'num', value: 1 }],
                    color: { argb: 'FF63C384' },
                    border: true,
                    borderColor: { argb: 'FF63C384' }
                }]
            })
        }

        ws.getRow(row).height = 17
        row++

        // ─── Anomalies section ────────────────────────────────────────────────
        ws.getRow(row).height = 30
        merge(`A${row}:J${row}`)
        cell(`A${row}`, tl('auditAnomaliesTitle'), {
            bold: true, size: 13, fill: 'FFEFDADD', align: 'center'
        })
        row++

        ws.getRow(row).height = 17
        row++

        ws.getRow(row).height = 20
        cell(`A${row}`, tl('auditTheme'), { bold: true, size: 13, fill: 'FFEFDADD', align: 'center' })
        cell(`B${row}`, tl('controlSubject'), { bold: true, size: 13, fill: 'FFEFDADD', align: 'center' })
        merge(`C${row}:D${row}`)
        cell(`C${row}`, tl('auditorComment'), { bold: true, size: 13, fill: 'FFEFDADD', align: 'center' })
        merge(`E${row}:I${row}`)
        cell(`E${row}`, tl('auditorRecommendation'), { bold: true, size: 13, fill: 'FFEFDADD', align: 'center' })
        cell(`J${row}`, tl('plannedImplementationDate'), { bold: true, size: 13, fill: 'FFEFDADD', align: 'center' })
        row++

        const anomalies = controls.filter(ctrl => {
            const rateId = _.get(ctrl, 'conformityRate.id')
            const statusId = _.get(ctrl, 'status.id')
            return rateId && rateId !== '100' && !['notApplicable', 'notControlled'].includes(statusId)
        })

        const sorted = _.sortBy(anomalies, [
            ctrl => _.get(ctrl, 'auditControl.auditTheme.code') || '',
            ctrl => _.get(ctrl, 'auditControl.code') || ''
        ])

        // Group by theme to enable vertical cell merge
        const groups = []
        let lastThemeId = null
        sorted.forEach(ctrl => {
            const theme = _.get(ctrl, 'auditControl.auditTheme') || {}
            const themeId = theme.id || (theme._id && theme._id.toString())
            if (themeId !== lastThemeId) {
                groups.push({ theme, controls: [ctrl] })
                lastThemeId = themeId
            } else {
                groups[groups.length - 1].controls.push(ctrl)
            }
        })

        groups.forEach(group => {
            const firstRow = row
            const theme = group.theme || {}
            const themeLabel = `${theme.code || ''} - ${translateName(theme.name || '', lang)}`

            group.controls.forEach(ctrl => {
                ws.getRow(row).height = 60

                const controlName = translateName(_.get(ctrl, 'auditControl.name') || '', lang)
                cell(`B${row}`, controlName, { size: 11, wrapText: true })

                const rateId = _.get(ctrl, 'conformityRate.id')
                const bgColor = getAnomalyBgColor(rateId)

                merge(`C${row}:D${row}`)
                const commentCell = ws.getCell(`C${row}`)
                commentCell.value = stripHtml(_.get(ctrl, 'initialAuditorComment') || '')
                commentCell.font = { size: 11 }
                commentCell.alignment = { wrapText: true, vertical: 'middle' }
                if (bgColor) applyFill(commentCell, bgColor)

                merge(`E${row}:I${row}`)
                cell(`E${row}`, stripHtml(_.get(ctrl, 'auditorRecommendation') || ''), {
                    size: 11, wrapText: true
                })

                const implDate = ctrl.implementationDate
                if (implDate) {
                    const dateCell = ws.getCell(`J${row}`)
                    dateCell.value = new Date(implDate)
                    dateCell.font = { size: 11 }
                    dateCell.numFmt = 'DD/MM/YYYY'
                    dateCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
                }

                row++
            })

            const lastRow = row - 1
            if (lastRow > firstRow) merge(`A${firstRow}:A${lastRow}`)
            const themeCell = ws.getCell(`A${firstRow}`)
            themeCell.value = themeLabel
            themeCell.font = { bold: true, size: 13 }
            themeCell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true }
        })

        return workbook
    }
}

export async function generateAuditReportExcel({ auditPreparation, controls, language, context }, filename) {
    return new Promise((resolve, reject) => {
        global.excel.generateExcel(
            configureAuditReportWorkbook({ auditPreparation, controls, language, context }),
            filename,
            (err, file) => err ? reject(err) : resolve(file)
        )
    })
}